import { useEffect } from "react";
import { useMutation } from "@tanstack/react-query";

import {
  selectDates,
  selectOpponentsSelected,
  selectCategoriesSelected,
} from "@/global/store/data/gameFilters";
import { useAppStore } from "@/global/store";
import {
  eventsState,
  selectRegularEvents,
  selectEventsSelected,
  updateEventsSelected,
  updateEventsSelectedWithoutSort,
} from "@/global/store/data/purchaseSeats/events";
import { useCartHelpers } from "../useCartHelpers";
import {
  cartManagerState,
  selectCartManager,
} from "@/global/store/data/purchaseSeats/cartManager";
import { useAppSystem } from "@/global/hooks/useAppSystem";
import { inventoryTypeUIDs } from "@/global/config/constants";
import { getUpcomingEventsApi } from "@/global/api/events";
import { getPacksMappingApi } from "@/global/api/inventory";
import {
  handlePackDynamicAccountGroup,
  handleSetUpdatedAccountGroup,
} from "../Packs/packs.utils";
import { packsState } from "@/global/store/data/purchaseSeats/packs";
import { bucketsState } from "@/global/store/data/purchaseSeats/buckets";
import { selectPackageSelected } from "@/global/store/data/purchaseSeats/packages";
import type { EventType } from "@/global/store/data/types";
import { useRepsProductModal } from "../modals/RepsProductModal";
import { useSelectActiveEvent } from "../ChooseSeats/useSelectActiveEvent";
import { useInventoryPreviewModal } from "../InventoryPreview/InventoryPreviewModal";
import { selectUser } from "@/global/store/data/user";

export function useFindGames() {
  const { router, teamConfig, dispatch } = useAppSystem();
  const { teamSchema, buckets, packs, uiConfigs, inventoryType } = teamConfig;

  // modals
  const inventoryPreviewModal = useInventoryPreviewModal();
  const repsProductModal = useRepsProductModal();

  // apis
  const getEvents = useMutation(getUpcomingEventsApi);
  const cartState = useAppStore(selectCartManager);
  const getPacksMapping = useMutation(getPacksMappingApi);

  // states
  const regularEvents = useAppStore(selectRegularEvents);
  const selectedEvents = useAppStore(selectEventsSelected);
  const { activeEvent } = useSelectActiveEvent();
  const purchaseSeatTab = router.query.purchaseSeatTab as any;
  const urlPack = router.query.pack as string;
  const featuredEvents = regularEvents.filter(
    (itm) => itm.isFeaturedGame && !(itm.minEventSelectionRequired > 0)
  );
  // .filter((itm) => itm?.hasInventory);
  const upcomingEvents = regularEvents.filter((itm) => !(itm.minEventSelectionRequired > 0)); // prettier-ignore
  const selectedPackage = useAppStore(selectPackageSelected);
  const user = useAppStore(selectUser);
  const isBucketsEnabled = (!!buckets && buckets?.enabled) || false;
  const isPacksEnabled = (!!packs && packs?.enabled) || false;

  // event filters state
  const selectedOpponents = useAppStore(selectOpponentsSelected);
  const dates = useAppStore(selectDates);
  const selectedCategories = useAppStore(selectCategoriesSelected);

  // modals
  const isPkgConfirmOpen = Boolean(router.query.isPkgConfirmOpen);

  // helpers
  const { resetCartSelections } = useCartHelpers();

  // handlers
  const handleSelect = (event: EventType) => {
    if (uiConfigs?.findGames?.setEventsWithoutSorting) {
      dispatch(updateEventsSelectedWithoutSort(event));
    } else dispatch(updateEventsSelected(event));

    if (cartState.cart.some((itm) => itm.eventCode == event.eventCode)) {
      dispatch(
        cartManagerState.actions.removeEvent({ eventCode: event.eventCode })
      );
    }
  };

  const handleGetEvents = async () => {
    try {
      const payload = {
        teamSchema,
        eventFilters: {
          dates: dates,
          opponentNames: selectedOpponents,
          eventCodes: [],
          categories: selectedCategories,
        },
        userId: user?.userId,
      };
      await getEvents.mutateAsync(payload, {
        onSuccess: async ({ data }: any) => {
          dispatch(eventsState.actions.setEvents(data));

          // Auto-open preview modal when URL contains event code query param (SUITES only)
          const urlEventCode = router.query.eventCode as string | undefined;

          if (inventoryType === inventoryTypeUIDs.suiteLevelV2 && urlEventCode) {
            const matchedEvent = (data as EventType[]).find(
              (evt) => evt.eventCode === urlEventCode,
            );
            if (matchedEvent) {
              inventoryPreviewModal.open({ event: matchedEvent });
              // Remove eventCode from URL to prevent re-triggering on filter changes
              const { eventCode, ...restQuery } = router.query;
              router.replace({ query: restQuery }, undefined, { shallow: true });
            }
          }

          // setting initial buckets that comes from the server
          // prettier-ignore
          if (isBucketsEnabled && !!buckets) {
            const minId = Math.min(...buckets.list.map((itm) => itm.id)); // get the min id available
            const availableBucketEvents = buckets?.list.map((itm, idx) => ({
              id: itm.id,
              title: itm.name,
              description: itm.description,
              opened: itm.id == minId || buckets?.list[idx - 1]?.min === 0,
              disabled: buckets?.list[idx - 1]?.min === 0 ? false : itm.id != minId,
              events: data.filter(
                (event: EventType) => event.bucketId === itm.id
              ),
              min: buckets.list.find(b => b.id === itm.shouldUseMinAmountOfBucketId)?.min || itm.min,
              max: buckets.list.find(b => b.id === itm.shouldUseMaxAmountOfBucketId)?.max || itm.max,
              isMaxCountReached: false,
              remainingBucketLanguage: itm.remainingBucketLanguage,
              disableAccordionExpand: itm.disableAccordionExpand,
            }));

            dispatch(bucketsState.actions.setBuckets(availableBucketEvents)); // prettier-ignore
          }

          // setting initial packs + buckets that comes from the server
          // prettier-ignore
          if (isPacksEnabled && !!packs) {
            try {
              const { data: packsMappingData } = await getPacksMapping.mutateAsync({ teamSchema });
              const updatedPacks = packs.list.map((pack) => {
                const minId = Math.min(...pack.buckets.map((bucket) => bucket.id));
                const updatedBuckets = pack.buckets.map((bucket, idx) => {
                  const indexMinZero = pack.buckets[idx - 1]?.min === 0;
                  return {
                    ...bucket,
                    id: bucket.id,
                    title: bucket.title,
                    isMaxCountReached: false,
                    opened: indexMinZero ? true : bucket.id == minId,
                    disabled: indexMinZero ? false : bucket.id != minId,
                    description: bucket.description,
                    min: pack.buckets.find((b) => b.id == bucket.shouldUseMinAmountOfBucketId)?.min || bucket.min,
                    max: pack.buckets.find((b) => b.id == bucket.shouldUseMaxAmountOfBucketId)?.max || bucket.max,
                    events: data.filter((event: EventType) =>
                      packsMappingData.length > 0
                        ? packsMappingData.some(
                            (itm: { packId: number; bucketId: number; eventCode: string; }) =>
                              itm.packId === pack.id &&
                              itm.bucketId === bucket.id &&
                              itm.eventCode === event.eventCode 
                          )
                        : event.bucketId === bucket.id)
                        .map((event: EventType) => ({
                          ...event,
                          // Scope the lookup to THIS pack and bucket. The same
                          // event legitimately sits in several packs (and at a
                          // different tier position in each), so matching on
                          // eventCode alone stamps the bucketId of whichever
                          // mapping row happens to come first — which then makes
                          // usePacks count the selection against the wrong tier,
                          // so the tier never completes and Continue stays
                          // disabled. Mirrors the filter above.
                          bucketId:
                            packsMappingData.find(
                              (itm: {
                                packId: number;
                                bucketId: number;
                                eventCode: string;
                              }) =>
                                itm.packId === pack.id &&
                                itm.bucketId === bucket.id &&
                                itm.eventCode === event.eventCode
                            )?.bucketId || event.bucketId,
                        })),
                  };
                });
                
                // need to change the default selected pack to the one from the url param
                if (urlPack) {
                  return { ...pack, buckets: updatedBuckets, selected: pack.amount === Number(urlPack) };
                }
                
                // if there's no url param, select the one with defaultSelected true
                return { ...pack, buckets: updatedBuckets, selected: !!pack.defaultSelected };
              });
              dispatch(packsState.actions.set(updatedPacks));
  
              // setting initial updated account group
              const pack = updatedPacks.find((pack) => pack.selected);
              if (pack) {
                if (pack?.dynamicAccountGroup && user?.accountGroupIds?.length > 0) handlePackDynamicAccountGroup(user, pack, teamSchema);
                else {
                  if (String(pack?.updatedAccountGroup)) handleSetUpdatedAccountGroup([String(pack.updatedAccountGroup)], teamSchema);
                }
              }
            } catch (error) {
              console.log(error);
            }
          }
        },
      });
    } catch (error) {
      console.log(error);
    } finally {
      dispatch(eventsState.actions.setIsLoading(false));
    }
  };

  const onClickPreview = (event: EventType) => {
    inventoryPreviewModal.open({ event });
  };

  // side effects
  useEffect(() => {
    handleGetEvents();
  }, [dates, selectedOpponents, selectedCategories]);

  useEffect(() => {
    resetCartSelections();

    // resetting the reps products modal to pop up again
    repsProductModal.set({ hasModalOpened: false });
  }, []);

  // return
  return {
    states: {
      regularEvents,
      selectedEvents,
      activeEvent,
      purchaseSeatTab,
      featuredEvents,
      upcomingEvents,
      selectedPackage,
      isPkgConfirmOpen,
    },

    apis: {
      getEvents,
      getPacksMapping,
    },
    handlers: {
      handleSelect,
      handleGetEvents,
      onClickPreview,
    },
  };
}
